<template>
  <div class="dashboard-container">
    <!-- Judul & Aksi -->
    <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
      <div class="page-title mb-0">
        <i class="bi bi-award"></i> Perkiraan Medali Berdasarkan Bagan
      </div>

      <div class="d-flex gap-2 align-items-center mt-2 mt-sm-0 no-print">
        <button
          type="button"
          class="ui-btn ui-btn--secondary"
          :disabled="memuat || !tournamentId"
          @click="cetakLaporan"
        >
          <i class="bi bi-printer"></i> Cetak Laporan
        </button>
      </div>
    </div>

    <!-- Pilih Turnamen -->
    <section class="ui-card mb-4 no-print">
      <div class="ui-card__body">
        <label class="ui-field mb-0">
          <span class="ui-label">Pilih Acara / Turnamen:</span>
          <select v-model="tournamentId" class="ui-input" @change="muatDataMedali">
            <option value="">Pilih Acara</option>
            <option v-for="t in turnamen" :key="t.id" :value="t.id">{{ t.name }}</option>
          </select>
        </label>
      </div>
    </section>

    <!-- Header Dokumen Khusus Cetak -->
    <div class="print-header">
      <h1 class="print-title">LAPORAN ESTIMASI & ALOKASI MEDALI PERTANDINGAN</h1>
      <p class="print-subtitle">{{ dataTurnamen?.nama || 'Kejuaraan Pencak Silat' }}</p>
      <p class="print-meta" v-if="dataTurnamen?.lokasi">
        Lokasi: {{ dataTurnamen.lokasi }} &middot; Tanggal: {{ formatTanggal(dataTurnamen.tanggal_mulai) }} s/d {{ formatTanggal(dataTurnamen.tanggal_selesai) }}
      </p>
    </div>

    <!-- Indikator Memuat -->
    <div v-if="memuat" class="ui-card p-4 text-center">
      <div class="ui-spinner mb-2"></div>
      <p class="text-muted mb-0">Menghitung alokasi medali dari bagan pertandingan…</p>
    </div>

    <template v-else-if="tournamentId">
      <!-- ═══════════════ RINGKASAN METRIK MEDALI ═══════════════ -->
      <div class="row g-3 mb-4">
        <!-- Emas -->
        <div class="col-6 col-lg-3">
          <div class="ui-card stat-card stat-card--gold">
            <div class="stat-card__icon">🥇</div>
            <div class="stat-card__content">
              <span class="stat-card__label">Medali Emas (Juara 1)</span>
              <strong class="stat-card__value">{{ ringkasan.total_emas }}</strong>
              <small class="stat-card__sub">1 keping per nomor/pool</small>
            </div>
          </div>
        </div>

        <!-- Perak -->
        <div class="col-6 col-lg-3">
          <div class="ui-card stat-card stat-card--silver">
            <div class="stat-card__icon">🥈</div>
            <div class="stat-card__content">
              <span class="stat-card__label">Medali Perak (Juara 2)</span>
              <strong class="stat-card__value">{{ ringkasan.total_perak }}</strong>
              <small class="stat-card__sub">1 keping untuk babak final</small>
            </div>
          </div>
        </div>

        <!-- Perunggu -->
        <div class="col-6 col-lg-3">
          <div class="ui-card stat-card stat-card--bronze">
            <div class="stat-card__icon">🥉</div>
            <div class="stat-card__content">
              <span class="stat-card__label">Perunggu (Juara 3 Bersama)</span>
              <strong class="stat-card__value">{{ ringkasan.total_perunggu }}</strong>
              <small class="stat-card__sub">2 keping untuk semifinalis</small>
            </div>
          </div>
        </div>

        <!-- Total Medali -->
        <div class="col-6 col-lg-3">
          <div class="ui-card stat-card stat-card--total">
            <div class="stat-card__icon">🏅</div>
            <div class="stat-card__content">
              <span class="stat-card__label">Total Keping Medali</span>
              <strong class="stat-card__value">{{ ringkasan.total_medali }}</strong>
              <small class="stat-card__sub">Kebutuhan logistik medali</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Keterangan Aturan Medali -->
      <div class="ui-card mb-4 p-3 info-box no-print">
        <div class="d-flex gap-2 align-items-start">
          <i class="bi bi-info-circle-fill text-accent fs-5"></i>
          <div>
            <strong>Aturan Kalkulasi Medali Pencak Silat:</strong>
            <ul class="mb-0 ps-3 mt-1 text-muted" style="font-size: 0.875rem">
              <li><strong>Babak Final (2 Peserta)</strong>: 1 Emas + 1 Perak (Total 2 Medali).</li>
              <li><strong>Babak Semifinal / Lebih (>= 3 Peserta)</strong>: 1 Emas + 1 Perak + 2 Perunggu (Juara 3 Bersama).</li>
              <li><strong>1 Peserta (Bye / Walkover)</strong>: 1 Emas.</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- ═══════════════ NAVIGASI TAB ═══════════════ -->
      <div class="tab-nav mb-3 no-print">
        <button
          type="button"
          class="tab-nav__btn"
          :class="{ 'is-active': activeTab === 'detail' }"
          @click="activeTab = 'detail'"
        >
          <i class="bi bi-diagram-3"></i> Rincian per Pool / Bagan ({{ detailPools.length }})
        </button>
        <button
          type="button"
          class="tab-nav__btn"
          :class="{ 'is-active': activeTab === 'kategori' }"
          @click="activeTab = 'kategori'"
        >
          <i class="bi bi-tags"></i> Rekapitulasi per Kategori ({{ rekapKategori.length }})
        </button>
        <button
          type="button"
          class="tab-nav__btn"
          :class="{ 'is-active': activeTab === 'kontingen' }"
          @click="activeTab = 'kontingen'"
        >
          <i class="bi bi-flag"></i> Potensi Kontingen ({{ rekapKontingen.length }})
        </button>
      </div>

      <!-- ═══════════════ TAB 1: RINCIAN PER POOL / BAGAN ═══════════════ -->
      <section v-show="activeTab === 'detail' || isPrinting" class="ui-card mb-4 print-section">
        <div class="ui-card__body">
          <div class="d-flex flex-wrap justify-content-between align-items-center mb-3 no-print">
            <h2 class="section-title mb-0" style="font-size: 1.1rem">
              <i class="bi bi-diagram-3"></i> Rincian Medali Setiap Pool Pertandingan
            </h2>

            <div class="d-flex flex-wrap gap-2 align-items-center mt-2 mt-sm-0">
              <!-- Filter Jenis -->
              <select v-model="filterJenis" class="ui-input ui-input--sm" style="width: auto">
                <option value="">Semua Nomor (Tanding & Seni)</option>
                <option value="Tanding">Tanding</option>
                <option value="Seni">Seni</option>
              </select>

              <!-- Filter Usia -->
              <select v-model="filterUsia" class="ui-input ui-input--sm" style="width: auto">
                <option value="">Semua Kelompok Usia</option>
                <option v-for="u in daftarUsia" :key="u" :value="u">{{ u }}</option>
              </select>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-hover table-bordered custom-table mb-0">
              <thead>
                <tr>
                  <th style="width: 50px">No</th>
                  <th>Nomor</th>
                  <th>Pool / Kelas</th>
                  <th>Kategori Pertandingan</th>
                  <th>Kelompok Usia</th>
                  <th>Peserta</th>
                  <th>Babak Bagan</th>
                  <th class="text-center" style="width: 70px">🥇 Emas</th>
                  <th class="text-center" style="width: 70px">🥈 Perak</th>
                  <th class="text-center" style="width: 75px">🥉 Perunggu</th>
                  <th class="text-center" style="width: 75px">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!filteredPools.length">
                  <td colspan="11" class="text-center py-4 text-muted">
                    Tidak ada data pool yang cocok dengan filter.
                  </td>
                </tr>
                <tr v-for="(p, idx) in filteredPools" :key="p.id">
                  <td class="text-center">{{ idx + 1 }}</td>
                  <td>
                    <span class="badge" :class="p.jenis_nomor === 'Tanding' ? 'badge--flame' : 'badge--gold'">
                      {{ p.jenis_nomor }}
                    </span>
                  </td>
                  <td>
                    <strong>{{ p.nama_pool }}</strong>
                    <div class="text-muted small" v-if="p.kelas !== '-'">{{ p.kelas }}</div>
                  </td>
                  <td>{{ p.kategori }}</td>
                  <td>{{ p.kelompok_usia }}</td>
                  <td class="text-center">{{ p.jumlah_peserta }}</td>
                  <td>
                    <span class="badge badge--subtle">{{ p.babak_tertinggi }}</span>
                  </td>
                  <td class="text-center fw-bold text-gold">{{ p.emas }}</td>
                  <td class="text-center fw-bold text-silver">{{ p.perak }}</td>
                  <td class="text-center fw-bold text-bronze">{{ p.perunggu }}</td>
                  <td class="text-center fw-bold text-dark bg-light">{{ p.total_medali }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="table-active fw-bold">
                  <td colspan="7" class="text-end">Subtotal Halaman:</td>
                  <td class="text-center text-gold">{{ subtotalPools.emas }}</td>
                  <td class="text-center text-silver">{{ subtotalPools.perak }}</td>
                  <td class="text-center text-bronze">{{ subtotalPools.perunggu }}</td>
                  <td class="text-center bg-warning-subtle">{{ subtotalPools.total }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </section>

      <!-- ═══════════════ TAB 2: REKAPITULASI KATEGORI ═══════════════ -->
      <section v-show="activeTab === 'kategori' || isPrinting" class="ui-card mb-4 print-section">
        <div class="ui-card__body">
          <h2 class="section-title mb-3" style="font-size: 1.1rem">
            <i class="bi bi-tags"></i> Rekapitulasi Kebutuhan Medali per Kategori
          </h2>

          <div class="table-responsive">
            <table class="table table-hover table-bordered custom-table mb-0">
              <thead>
                <tr>
                  <th style="width: 50px">No</th>
                  <th>Kategori Pertandingan</th>
                  <th>Kelompok Usia</th>
                  <th>Jenis</th>
                  <th class="text-center">Jumlah Pool</th>
                  <th class="text-center" style="width: 80px">🥇 Emas</th>
                  <th class="text-center" style="width: 80px">🥈 Perak</th>
                  <th class="text-center" style="width: 90px">🥉 Perunggu</th>
                  <th class="text-center" style="width: 90px">Total Medali</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!rekapKategori.length">
                  <td colspan="9" class="text-center py-4 text-muted">Belum ada data rekap.</td>
                </tr>
                <tr v-for="(rk, idx) in rekapKategori" :key="idx">
                  <td class="text-center">{{ idx + 1 }}</td>
                  <td><strong>{{ rk.kategori }}</strong></td>
                  <td>{{ rk.kelompok_usia }}</td>
                  <td>
                    <span class="badge" :class="rk.jenis_nomor === 'Tanding' ? 'badge--flame' : 'badge--gold'">
                      {{ rk.jenis_nomor }}
                    </span>
                  </td>
                  <td class="text-center">{{ rk.jumlah_pool }}</td>
                  <td class="text-center fw-bold text-gold">{{ rk.emas }}</td>
                  <td class="text-center fw-bold text-silver">{{ rk.perak }}</td>
                  <td class="text-center fw-bold text-bronze">{{ rk.perunggu }}</td>
                  <td class="text-center fw-bold bg-light">{{ rk.total_medali }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="table-active fw-bold">
                  <td colspan="4" class="text-end">TOTAL KESELURUHAN:</td>
                  <td class="text-center">{{ ringkasan.total_pool }} Pool</td>
                  <td class="text-center text-gold">{{ ringkasan.total_emas }}</td>
                  <td class="text-center text-silver">{{ ringkasan.total_perak }}</td>
                  <td class="text-center text-bronze">{{ ringkasan.total_perunggu }}</td>
                  <td class="text-center bg-warning-subtle text-primary">{{ ringkasan.total_medali }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </section>

      <!-- ═══════════════ TAB 3: POTENSI KONTINGEN ═══════════════ -->
      <section v-show="activeTab === 'kontingen' && !isPrinting" class="ui-card mb-4 no-print">
        <div class="ui-card__body">
          <h2 class="section-title mb-3" style="font-size: 1.1rem">
            <i class="bi bi-flag"></i> Potensi Perolehan Medali Kontingen
          </h2>
          <p class="text-muted small">
            Estimasi jumlah atlet per kontingen yang berada dalam bagan pertandingan dan berpeluang memperebutkan medali.
          </p>

          <div class="table-responsive">
            <table class="table table-hover table-bordered custom-table mb-0">
              <thead>
                <tr>
                  <th style="width: 50px">No</th>
                  <th>Nama Kontingen</th>
                  <th class="text-center">Atlet di Bagan</th>
                  <th class="text-center" style="width: 100px">Peluang Emas</th>
                  <th class="text-center" style="width: 100px">Peluang Perak</th>
                  <th class="text-center" style="width: 110px">Peluang Perunggu</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!rekapKontingen.length">
                  <td colspan="6" class="text-center py-4 text-muted">Belum ada data kontingen di bagan.</td>
                </tr>
                <tr v-for="(k, idx) in rekapKontingen" :key="idx">
                  <td class="text-center">{{ idx + 1 }}</td>
                  <td><strong>{{ k.kontingen }}</strong></td>
                  <td class="text-center">{{ k.jumlah_atlet_bagan }}</td>
                  <td class="text-center text-gold fw-bold">{{ k.potensi_emas }}</td>
                  <td class="text-center text-silver fw-bold">{{ k.potensi_perak }}</td>
                  <td class="text-center text-bronze fw-bold">{{ k.potensi_perunggu }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- Tanda Tangan Cetak -->
      <div class="print-signatures">
        <div class="row">
          <div class="col-6 text-center">
            <p>Mengetahui,<br><strong>Technical Delegate (TD)</strong></p>
            <div style="height: 60px"></div>
            <p>( ..................................................... )</p>
          </div>
          <div class="col-6 text-center">
            <p>Dibuat Oleh,<br><strong>Ketua Panitia Pelaksana</strong></p>
            <div style="height: 60px"></div>
            <p>( ..................................................... )</p>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import http from '@/services/http';
import { useToast } from 'vue-toastification';

export default {
  name: 'PerkiraanMedaliPage',

  setup() {
    return { toast: useToast() };
  },

  data() {
    return {
      turnamen: [],
      tournamentId: '',
      memuat: false,
      isPrinting: false,
      activeTab: 'detail',
      filterJenis: '',
      filterUsia: '',
      dataTurnamen: null,
      ringkasan: {
        total_emas: 0,
        total_perak: 0,
        total_perunggu: 0,
        total_medali: 0,
        total_pool_tanding: 0,
        total_pool_seni: 0,
        total_pool: 0,
      },
      detailPools: [],
      rekapKategori: [],
      rekapKontingen: [],
    };
  },

  computed: {
    daftarUsia() {
      const u = new Set();
      this.detailPools.forEach((p) => {
        if (p.kelompok_usia && p.kelompok_usia !== '-') u.add(p.kelompok_usia);
      });
      return Array.from(u);
    },

    filteredPools() {
      return this.detailPools.filter((p) => {
        if (this.filterJenis && p.jenis_nomor !== this.filterJenis) return false;
        if (this.filterUsia && p.kelompok_usia !== this.filterUsia) return false;
        return true;
      });
    },

    subtotalPools() {
      let emas = 0;
      let perak = 0;
      let perunggu = 0;
      this.filteredPools.forEach((p) => {
        emas += p.emas;
        perak += p.perak;
        perunggu += p.perunggu;
      });
      return { emas, perak, perunggu, total: emas + perak + perunggu };
    },
  },

  mounted() {
    document.title = 'Perkiraan Medali';
    this.muatAwal();
  },

  methods: {
    async muatAwal() {
      try {
        const { data } = await http.get('/tournaments', { params: { fetch_all: true } });
        this.turnamen = Array.isArray(data) ? data : data?.data ?? [];
        if (this.$route.params.id) {
          this.tournamentId = this.$route.params.id;
        } else if (this.turnamen.length > 0) {
          this.tournamentId = this.turnamen[0].id;
        }
        if (this.tournamentId) {
          this.muatDataMedali();
        }
      } catch (e) { /* handled by interceptor */ }
    },

    async muatDataMedali() {
      if (!this.tournamentId) return;
      this.memuat = true;
      try {
        const { data } = await http.get(`/perkiraan-medali/${this.tournamentId}`);
        this.dataTurnamen = data.turnamen;
        this.ringkasan = data.ringkasan;
        this.detailPools = data.detail_pools || [];
        this.rekapKategori = data.rekap_kategori || [];
        this.rekapKontingen = data.rekap_kontingen || [];
      } catch (e) {
        this.toast.error('Gagal memuat perkiraan medali.');
      } finally {
        this.memuat = false;
      }
    },

    formatTanggal(tgl) {
      if (!tgl) return '-';
      try {
        const d = new Date(tgl);
        return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
      } catch (e) {
        return tgl;
      }
    },

    async cetakLaporan() {
      this.isPrinting = true;
      await this.$nextTick();
      setTimeout(() => {
        window.print();
        this.isPrinting = false;
      }, 300);
    },
  },
};
</script>

<style scoped>
.page-title {
  font-size: var(--text-xl);
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.stat-card {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-md);
  border: 1px solid var(--border-subtle);
  transition: transform 0.15s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-card__icon {
  font-size: 2.2rem;
  line-height: 1;
}

.stat-card__content {
  display: flex;
  flex-direction: column;
}

.stat-card__label {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-secondary);
}

.stat-card__value {
  font-size: 1.6rem;
  font-weight: 800;
  line-height: 1.1;
  color: var(--text-primary);
}

.stat-card__sub {
  font-size: 0.72rem;
  color: var(--text-muted);
}

.stat-card--gold { border-left: 4px solid #f59e0b; background: linear-gradient(135deg, rgba(245, 158, 11, 0.08), transparent); }
.stat-card--silver { border-left: 4px solid #9ca3af; background: linear-gradient(135deg, rgba(156, 163, 175, 0.08), transparent); }
.stat-card--bronze { border-left: 4px solid #d97706; background: linear-gradient(135deg, rgba(217, 119, 6, 0.08), transparent); }
.stat-card--total { border-left: 4px solid var(--accent); background: linear-gradient(135deg, rgba(232, 72, 8, 0.08), transparent); }

.text-gold { color: #d97706 !important; }
.text-silver { color: #6b7280 !important; }
.text-bronze { color: #b45309 !important; }

.badge--flame { background: rgba(232, 72, 8, 0.12); color: #e84808; font-weight: 600; padding: 4px 8px; border-radius: 4px; }
.badge--gold { background: rgba(245, 158, 11, 0.12); color: #d97706; font-weight: 600; padding: 4px 8px; border-radius: 4px; }
.badge--subtle { background: var(--bg-sunken); color: var(--text-secondary); font-weight: 500; padding: 4px 8px; border-radius: 4px; }

.tab-nav {
  display: flex;
  gap: var(--space-2);
  border-bottom: 1px solid var(--border-subtle);
  padding-bottom: var(--space-1);
}

.tab-nav__btn {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-secondary);
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;
}

.tab-nav__btn:hover {
  color: var(--accent);
}

.tab-nav__btn.is-active {
  color: var(--accent);
  border-bottom-color: var(--accent);
}

.custom-table th {
  background: var(--bg-sunken);
  color: var(--text-primary);
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  vertical-align: middle;
}

.custom-table td {
  vertical-align: middle;
  font-size: 0.875rem;
}

.info-box {
  background: rgba(232, 72, 8, 0.05);
  border: 1px solid rgba(232, 72, 8, 0.2);
  border-radius: var(--radius-md);
}

/* ── Tampilan Cetak ── */
.print-header { display: none; }
.print-signatures { display: none; }

@media print {
  .no-print { display: none !important; }
  .print-header {
    display: block !important;
    text-align: center;
    margin-bottom: 20px;
    border-bottom: 2px solid #000;
    padding-bottom: 10px;
  }
  .print-title { font-size: 16pt; font-weight: bold; margin: 0; }
  .print-subtitle { font-size: 13pt; margin: 4px 0; }
  .print-meta { font-size: 9pt; color: #555; margin: 0; }
  .print-signatures {
    display: block !important;
    margin-top: 30px;
    page-break-inside: avoid;
  }
  .custom-table th { background: #f3f4f6 !important; color: #000 !important; }
  .custom-table td { color: #000 !important; }
  .print-section {
    box-shadow: none !important;
    border: none !important;
    padding: 0 !important;
    margin-bottom: 25px !important;
  }
}
</style>